import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { type Atom, useAtomValue } from "jotai"
import { type CustomChartSerie } from "modules/chart-builder/types"
import { getProjectsFromFilters } from "modules/chart-builder/utils/get-projects-from-filter"
import { toId } from "modules/market-sectors/utils"
import { useMetricsConfigurationList } from "modules/projects/hooks/useMetricsConfigurationList"
import { useProjectsList } from "modules/projects/hooks/useProjectsList"
import { capitalize } from "utils/lodash/capitalize"
import { useMetricMetadataByProjectSlug } from "../../../modules/projects/OverviewTab/useMetricMetadataForProject"

type ProjectSpecificMetricSource = {
  source: string
  metricSlug: string
  metricName: string
  type: "project-specific"
  projectSlug: string
  projectName: string
}

type GenericMetricSource = {
  source: string
  metricSlug: string
  metricName: string
  type: "generic"
}

type MetricSource = ProjectSpecificMetricSource | GenericMetricSource

const isProjectSpecificMetricSource = (
  source: MetricSource
): source is ProjectSpecificMetricSource => source.type === "project-specific"

type ProjectSpecificMetricDefinition = {
  definition: string
  metricSlug: string
  metricName: string
  type: "project-specific"
  projectSlug: string
  projectName: string
}

type GenericMetricDefinition = {
  definition: string
  metricSlug: string
  metricName: string
  type: "generic"
}

type MetricDefinition =
  | ProjectSpecificMetricDefinition
  | GenericMetricDefinition

const isProjectSpecificMetricDefinition = (
  definition: MetricDefinition
): definition is ProjectSpecificMetricDefinition =>
  definition.type === "project-specific"

/**
 * Util to join an array of strings with commas, and "and" before the last item.
 */

export const joinWithPunctuation = (items: Array<string>) => {
  if (items.length === 0) {
    return ""
  } else if (items.length === 1) {
    return items[0]
  } else if (items.length === 2) {
    return `${items[0]} and ${items[1]}`
  }

  const allButLast = items.slice(0, -1).join(", ")
  const lastItem = items[items.length - 1]
  return allButLast + ", and " + lastItem
}

/**
 * Util to group an array of objects by a given key.
 */

function groupBy<T extends Record<string, any>, const K extends keyof T>(
  items: T[],
  keyToGroupBy: K
) {
  const groupedItems: Record<string, Array<T>> = {}

  // Group items by the specified key
  for (const item of items) {
    const key = String(item[keyToGroupBy])
    if (!groupedItems[key]) {
      groupedItems[key] = []
    }
    groupedItems[key].push(item)
  }

  return groupedItems
}

/**
 * Returns an object of sources and definitions for each metric-project
 * combination. Sources and definitions can either be project-specific or
 * generic.
 */

const useSourcesAndDefinitions = (
  metricCombinations: Array<{
    metricSlug: string
    projectSlug?: string
  }>
) => {
  const {
    data: metricConfigurations = [],
    isLoading: isLoadingMetricConfigurations,
  } = useMetricsConfigurationList()
  const metricConfigurationsBySlug = toDictionary(metricConfigurations, "slug")

  // Get project-specific definitions and sources
  const { data: projectsList = [], isLoading: isLoadingProjectsList } =
    useProjectsList()
  const projectsBySlug = toDictionary(projectsList, "slug")

  const projectMetricCombinations = (
    isLoadingProjectsList
      ? []
      : metricCombinations.filter((item) => !!item.projectSlug)
  ) as Array<{
    metricSlug: string
    projectSlug: string
  }>

  const uniqueProjectSlugs = Array.from(
    new Set(
      projectMetricCombinations
        .map((item) => item.projectSlug)
        .filter((item) => item !== undefined)
    )
  )
  const {
    data: metricMetadataByProjectSlug = {},
    isLoading: isLoadingMetricMetadata,
  } = useMetricMetadataByProjectSlug()

  if (
    isLoadingMetricMetadata ||
    isLoadingMetricConfigurations ||
    isLoadingProjectsList
  ) {
    return {
      sources: [],
      definitions: [],
    }
  }

  const projectMetricSources: Array<ProjectSpecificMetricSource> =
    projectMetricCombinations
      .map((item) => {
        if (!item) {
          return null
        }

        const metricSourcesForProject =
          metricMetadataByProjectSlug?.[item.projectSlug]?.metric_sources || []

        const projectSpecificSource = metricSourcesForProject?.find(
          (source) => source.metric_id === toId(item.metricSlug)
        )?.definition

        const metricConfiguration = metricConfigurationsBySlug.get(
          item.metricSlug
        )
        const project = projectsBySlug.get(item.projectSlug)

        if (!metricConfiguration) {
          throw new Error(
            `Metric configuration not found for metricSlug: ${item.metricSlug}`
          )
        }

        if (!project) {
          throw new Error(
            `Project not found for projectSlug: ${item.projectSlug}`
          )
        }

        return {
          type: "project-specific" as const,
          metricSlug: item.metricSlug,
          metricName: metricConfiguration.title,
          projectSlug: item.projectSlug,
          projectName: project.name,
          source:
            projectSpecificSource || metricConfiguration.sources || "Unknown",
        }
      })
      .filter((item) => item !== null)

  const projectMetricDefinitions: Array<ProjectSpecificMetricDefinition> =
    projectMetricCombinations
      .map((item) => {
        if (!item) {
          return null
        }

        const metricDefinitionsForProject =
          metricMetadataByProjectSlug?.[item.projectSlug]?.metric_definitions ||
          []

        const projectSpecificDefinition = metricDefinitionsForProject.find(
          (definition) => definition.metric_id === toId(item.metricSlug)
        )?.definition

        const metricConfiguration = metricConfigurationsBySlug.get(
          item.metricSlug
        )
        const project = projectsBySlug.get(item.projectSlug)

        if (!metricConfiguration) {
          throw new Error(
            `Metric configuration not found for metricSlug: ${item.metricSlug}`
          )
        }

        if (!project) {
          throw new Error(
            `Project not found for projectSlug: ${item.projectSlug}`
          )
        }

        return {
          type: "project-specific" as const,
          metricSlug: item.metricSlug,
          metricName: metricConfiguration.title,
          projectSlug: item.projectSlug,
          projectName: project.name,
          definition:
            projectSpecificDefinition ||
            metricConfiguration.definition ||
            "Unknown",
        }
      })
      .filter((item) => item !== null)

  // Get generic sources and definitions
  const genericMetricSources: Array<GenericMetricSource> = metricCombinations
    .filter((item) => !item.projectSlug)
    .map((metricCombination) => {
      const metricConfiguration = metricConfigurationsBySlug.get(
        metricCombination.metricSlug
      )
      if (!metricConfiguration || !metricConfiguration.sources) {
        return null
      }

      const isMetricAlreadyIncluded = projectMetricSources.some(
        (source) =>
          source !== null && source.metricSlug === metricCombination.metricSlug
      )

      if (isMetricAlreadyIncluded) {
        return null
      }

      return {
        type: "generic" as const,
        metricSlug: metricCombination.metricSlug,
        metricName: metricConfiguration.title,
        source: metricConfiguration.sources,
      }
    })
    .filter((item) => item !== null)

  const genericMetricDefinitions: Array<GenericMetricDefinition> =
    metricCombinations
      .filter((item) => !item.projectSlug)
      .map((metricCombination) => {
        const metricConfiguration = metricConfigurationsBySlug.get(
          metricCombination.metricSlug
        )
        if (!metricConfiguration || !metricConfiguration.definition) {
          return null
        }

        // Check if this metric is already included in project-specific definitions
        const isMetricAlreadyIncluded = projectMetricDefinitions.some(
          (definition) =>
            definition !== null &&
            definition.metricSlug === metricCombination.metricSlug
        )

        if (isMetricAlreadyIncluded) {
          return null
        }

        return {
          type: "generic" as const,
          metricSlug: metricCombination.metricSlug,
          metricName: metricConfiguration.title,
          definition: metricConfiguration.definition,
        }
      })
      .filter((item) => item !== null)

  return {
    sources: [...projectMetricSources, ...genericMetricSources],
    definitions: [...projectMetricDefinitions, ...genericMetricDefinitions],
  }
}

/**
 * For a given chart series, returns the sources and definitions from useSourcesAndDefinitions.
 */

const useSourcesAndDefinitionsForChartSeries = (
  serieSettings: Array<CustomChartSerie>,
  projectsLimit = 2
) => {
  const { data: projectsList = [], isLoading: isLoadingProjectsList } =
    useProjectsList()
  const projectsByDataId = toDictionary(projectsList, "data_id")

  const metricCombinations = (isLoadingProjectsList ? [] : serieSettings)
    .map((serie) => {
      const dataIdsFromFilters = getProjectsFromFilters(serie.filters)

      const projectMetricCombinations = dataIdsFromFilters
        .map((dataId) => projectsByDataId.get(dataId))
        .filter((project) => project !== undefined)
        .map((project) => ({
          metricSlug: serie.metric,
          projectSlug: project.slug,
        }))

      // If we don't have any project-specific metric combinations, we can just
      // return the metric slug to get a generic source and definition.
      // To avoid overwhelming the UI, we also fallback to the generic source and
      // definition if there are more than the projectsLimit of project-specific metric combinations.
      if (
        projectMetricCombinations.length === 0 ||
        projectMetricCombinations.length > projectsLimit
      ) {
        return {
          metricSlug: serie.metric,
        }
      }
      return projectMetricCombinations
    })
    .flat()

  return useSourcesAndDefinitions(metricCombinations)
}

/**
 * Returns a string of sources for a given array of metric sources.
 * Examples:
 * - Sources: TokenTerminal and CoinGecko
 */

const makeSourcesText = (metricSources: Array<MetricSource>) => {
  const itemsBySource = groupBy(metricSources, "source")
  const formattedSourcesList = Object.keys(itemsBySource)
  const hasMultiple = formattedSourcesList.length > 1
  return `Source${hasMultiple ? "" : "s"}: ${joinWithPunctuation(formattedSourcesList)}.`
}

/**
 * Returns a string of definitions for a given array of metric definitions.
 * Examples:
 * - Generic: Token trading volume defined as trading volume of the protocol’s governance token.
 * - Project-specific: Fees for Aave defined as the sum of all fees paid by Aave users.
 */

const makeDefinitionsText = (definitions: Array<MetricDefinition>) => {
  // Assumes there are no collisions where different metrics have the same definition.
  const itemsByDefinition = groupBy(definitions, "definition")

  return Object.entries(itemsByDefinition)
    .map(([definition, items]) => {
      let metricName = items[0].metricName
      if (items.every(isProjectSpecificMetricDefinition)) {
        metricName = `${items[0].metricName} (${joinWithPunctuation(items.map((item) => item.projectName))})`
      }

      const hideMetricName = definition.startsWith(metricName)

      return `${hideMetricName ? "" : `${metricName}:`} ${capitalize(definition)}`
    })
    .join(" ")
}

export function useSourcesAndDefinitionsTextForChartSeries({
  serieSettingsAtom,
}: {
  serieSettingsAtom: Atom<
    Promisable<Array<CustomChartSerie>> | Array<CustomChartSerie>
  >
}) {
  const serieSettings = useAtomValue(serieSettingsAtom)
  const sourcesAndDefinitions =
    useSourcesAndDefinitionsForChartSeries(serieSettings)

  // e.g. Sources: TokenTerminal
  const sourcesText = makeSourcesText(sourcesAndDefinitions.sources)
  // e.g. Fees defined as... Active loans for Aave defined as...
  const definitionsText = makeDefinitionsText(sourcesAndDefinitions.definitions)

  return `${sourcesText} ${definitionsText}`
}
